"use client";

import React from "react";
import { ArrowRight, Bot, Compass, FileSpreadsheet, Globe, Layers, Sparkles, TrendingUp } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
interface BeatItem {
  id: string;
  title: string;
  focus: string;
  description: string;
  cadence: string;
  icon: "marketing" | "ai" | "revops" | "macro";
}
const beats: BeatItem[] = [{
  id: "marketing",
  title: "B2B Marketing Transformation",
  focus: "Demand Architecture & Buyer Dynamics",
  description: "Deep dives into multi-touch attribution models, account-based orchestration, and brand resonance in saturated B2B categories.",
  cadence: "Bi-weekly briefings",
  icon: "marketing"
}, {
  id: "ai",
  title: "Applied Enterprise AI",
  focus: "Deployment, Governance & Scale",
  description: "Empirical case analyses of workflow automation, internal model safety guardrails, and measurable unit-economics of machine intelligence.",
  cadence: "Weekly analysis",
  icon: "ai"
}, {
  id: "revops",
  title: "Revenue Operations Architecture",
  focus: "Pipeline Systems & Tech Stacks",
  description: "Blueprint breakdowns for unified customer lifecycles, CRM consolidation, and cross-functional instrumentation.",
  cadence: "Monthly deep dive",
  icon: "revops"
}, {
  id: "macro",
  title: "Macro Industry Trends",
  focus: "Capital Allocation & Market Policy",
  description: "Quarterly macro perspectives on enterprise procurement cycles, global data privacy mandates, and software valuation benchmarks.",
  cadence: "Quarterly review",
  icon: "macro"
}];
const stats = [{
  label: "Active Editorial Contributors",
  value: "48+"
}, {
  label: "Quarterly Enterprise Readers",
  value: "120k"
}, {
  label: "Peer-Review Verification Rate",
  value: "100%"
}];
export default function Intro() {
  const shouldReduceMotion = useReducedMotion();
  const getBeatIcon = (type: BeatItem["icon"]) => {
    switch (type) {
      case "marketing":
        return <TrendingUp className="size-5 text-primary" />;
      case "ai":
        return <Bot className="size-5 text-primary" />;
      case "revops":
        return <Layers className="size-5 text-primary" />;
      case "macro":
        return <Globe className="size-5 text-primary" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="intro" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Masthead Rule Header */}
        <div className="mb-12 sm:mb-16">
          <div className="flex items-center justify-between gap-4 pb-3 border-b border-foreground">
            <div className="flex items-center gap-2">
              <Compass className="size-4 text-muted-foreground" />
              <span className="font-mono text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Editorial Agenda &bull; MinfoMedia Journal
              </span>
            </div>
            <span className="hidden sm:inline-block font-mono text-xs text-muted-foreground">
              Volume XXIV &bull; Core Coverage Beats
            </span>
          </div>
          <div className="h-0.5 border-b border-border/80 mt-1" />
        </div>

        {/* Editorial Framing Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 mb-16 sm:mb-20 items-start">
          {/* Main Editorial Text Column */}
          <motion.div variants={itemVariants} className="lg:col-span-7 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold tracking-wide uppercase">
              <Sparkles className="size-3.5" />
              <span>Independent B2B Research & Commentary</span>
            </div>

            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.18] tracking-tight text-foreground">
              Strategic viewpoints across the modern enterprise landscape.
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed font-sans">
              MinfoMedia investigates the structural forces reshaping enterprise
              organizations. Every article, commentary, and market briefing is
              developed by seasoned industry researchers without commercial bias
              or sponsored conclusions.
            </p>

            <div className="pt-2 flex flex-wrap items-center gap-4">
              <Button asChild className="rounded-sm px-6 py-3 font-medium">
                <Link to="/ResearchReportsPage">
                  <span>Explore research library</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" className="rounded-sm px-6 py-3 font-medium">
                <Link to="/NewsletterPage">
                  <span>Subscribe to briefings</span>
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Research Credibility Sidebar */}
          <motion.div variants={itemVariants} className="lg:col-span-5 bg-card text-card-foreground border border-border rounded-md p-6 sm:p-8 shadow-xs space-y-6">
            <div className="flex items-center gap-3 pb-4 border-b border-border">
              <FileSpreadsheet className="size-5 text-muted-foreground" />
              <div>
                <h3 className="font-serif text-lg font-semibold text-foreground">
                  Publishing standards
                </h3>
                <p className="text-xs text-muted-foreground font-sans">
                  Rigorous methodology, clear sourcing
                </p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground leading-relaxed">
              We separate primary analysis from vendor claims. Our reporting
              draws on anonymized cohort data, verified balance-sheet filings, and
              first-party executive interviews.
            </p>

            <div className="grid grid-cols-3 gap-3 pt-2">
              {stats.map((stat, i) => <div key={stat.label} data-index={i} className="bg-muted/60 rounded-sm p-3 text-center border border-border/60">
                  <p className="font-mono text-base sm:text-lg font-bold text-foreground">
                    {stat.value}
                  </p>
                  <p className="text-[11px] text-muted-foreground font-sans leading-tight mt-1">
                    {stat.label}
                  </p>
                </div>)}
            </div>
          </motion.div>
        </motion.div>

        {/* Coverage Beats Section Heading */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 pb-3 border-b border-border">
          <div>
            <span className="font-mono text-xs uppercase font-semibold text-muted-foreground tracking-wider">
              Systematic Coverage
            </span>
            <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground mt-1">
              Primary editorial beats
            </h2>
          </div>
          <p className="text-sm text-muted-foreground max-w-md font-sans">
            Structured analysis updated weekly across the four pillars of modern
            B2B operations.
          </p>
        </div>

        {/* Coverage Beats Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {beats.map((beat, i) => <motion.div key={beat.id} data-index={i} variants={itemVariants} className="h-full">
              <Card className="h-full rounded-md border border-border bg-card text-card-foreground shadow-xs hover:shadow-md transition-[box-shadow,border-color] duration-200 hover:border-foreground/30 flex flex-col justify-between p-6">
                <CardHeader className="p-0 gap-3">
                  <div className="flex items-center justify-between w-full">
                    <div className="p-2.5 rounded-sm bg-muted inline-flex items-center justify-center">
                      {getBeatIcon(beat.icon)}
                    </div>
                    <span className="font-mono text-[11px] px-2 py-0.5 rounded-sm bg-muted text-muted-foreground border border-border/80">
                      {beat.cadence}
                    </span>
                  </div>

                  <div>
                    <span className="text-[11px] font-mono uppercase text-muted-foreground tracking-wider block mb-1">
                      {beat.focus}
                    </span>
                    <CardTitle className="font-serif text-lg font-semibold text-foreground leading-snug">
                      {beat.title}
                    </CardTitle>
                  </div>
                </CardHeader>

                <CardContent className="p-0 pt-3 flex-1">
                  <CardDescription className="text-sm text-muted-foreground font-sans leading-relaxed">
                    {beat.description}
                  </CardDescription>
                </CardContent>

                <div className="pt-5 mt-4 border-t border-border/80 flex items-center justify-between">
                  <Link to="/IndustryInsightsPage" className="inline-flex items-center text-xs font-semibold text-foreground hover:text-muted-foreground transition-colors group">
                    <span>Read latest briefs</span>
                    <ArrowRight className="size-3.5 ml-1 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}